/**
 * Plain-language explanations for the short attack-class labels used
 * across Security · Summary and the sender detail panel.
 *
 * Why this file exists
 * --------------------
 * The server renders a short, user-visible label for each threat class
 * (e.g. "Tried path traversal", "Tried Log4Shell", "Looked for secret
 * files"). Those labels are precise but not self-explanatory for
 * merchants who aren't security-fluent. This module maps the label text
 * to one short plain-language sentence that the dashboard can render
 * inline (muted) or inside a disclosure.
 *
 * It is NEVER used as a tooltip / `title` attribute — an existing test
 * forbids explanatory titles. The caller renders the plain line as
 * visible text next to the label.
 *
 * The map is intentionally keyed by the FULL short label string the
 * server returns (same strings as `CLASS_SHORT_LABEL` in
 * `services/api/src/threat/class-labels.ts`), so a new class landing
 * on the server without a plain-language entry here degrades to
 * returning `null`. A regular case-insensitive match is used so a
 * minor copy drift ("Posed as a known crawler" vs the same with a
 * trailing period) still resolves.
 */

const MAP: Readonly<Record<string, string>> = Object.freeze({
  // Secret / backup / config file scans.
  'looked for secret files': 'tried to open passwords and keys left in config files',
  'looked for backup files': 'tried to download old backup files that may hold passwords',
  'used a credential scanner': 'ran a tool that hunts for leaked passwords and keys',
  'probed debug pages': 'tried to open developer pages that leak system details',

  // Script-upload / disguised exec.
  'tried to run a script in uploads': 'tried to run a program they uploaded to your site',
  'tried a disguised script file': 'uploaded a file named like a picture but built to run as code',
  'looked for webshells': 'checked for a backdoor they or someone else may have planted',

  // Plugin / vulnerable component.
  'probed a vulnerable plugin': 'tried a plugin known to have a public security hole',

  // Injection family.
  'tried path traversal': 'tried to read files outside your site folder',
  'tried sql injection': 'tried to trick your database into leaking or changing data',
  'tried shell commands': 'tried to run system commands through a web form',
  'tried template injection': 'tried to run code through a page template',
  'tried log4shell': 'tried the Log4Shell flaw to run code through a logger',

  // Enumeration / scanning.
  'listed wordpress users': 'asked WordPress for the list of account names',
  'repeated login attempts': 'guessed passwords on the login form',
  'scanned for missing pages': 'rapidly asked for addresses that do not exist',

  // Impersonation.
  'posed as a known crawler': 'claimed to be Googlebot or similar, but is not',

  // ADR-0260 Slice 2 — behaviour drift on a verified agent. Server label
  // is "Agent behaviour changed" (CLASS_SHORT_LABEL); plainClassLine
  // lowercases before match.
  'agent behaviour changed': 'a verified agent is behaving differently from its usual pattern on this site',

  // Generic fallback label the server sends when a class is unknown.
  'attack-shaped request': 'sent a request shaped like a known attack',
});

/**
 * Returns a one-sentence plain-language description for the given
 * class label, or `null` when the label is unmapped. The caller renders
 * `null` as nothing (no visible fallback — don't invent copy).
 *
 * Matching is case-insensitive and tolerant of a trailing period or a
 * trailing " ×N" count suffix the summary line glues on.
 */
export function plainClassLine(label: string | null | undefined): string | null {
  if (typeof label !== 'string') return null;
  // Strip "×N" / "x N" trailing counts the summary line attaches.
  let key = label.replace(/\s*[×x]\s*\d+\s*$/i, '');
  key = key.trim().replace(/[.!?]+$/, '').toLowerCase();
  if (key.length === 0) return null;
  const hit = MAP[key];
  return typeof hit === 'string' ? hit : null;
}
